<template>
  <AuthContainer>
    <SpinnerLoader v-if="verifyingToken"></SpinnerLoader>
    <div :class="verifyingToken ? 'opacity-0' : 'opacity-100'" class="max-w-md w-full my-8">
      <div class="text-left mb-8">
        <h1 class="text-3xl md:text-4xl font-semibold mb-4 text-center">
          {{ $t('auth.resetPassword.title') }}
        </h1>
        <p v-if="!invalidToken" class="text-gray-700 text-lg">
          {{ $t('auth.resetPassword.subtitle') }}
        </p>
      </div>

      <form v-if="!invalidToken" @submit.prevent="onSubmit" class="space-y-6 text-sm">
        <BaseInput
          id="password"
          :label="$t('auth.resetPassword.form.password')"
          type="password"
          :placeholder="$t('auth.signIn.form.passwordPlaceholder')"
          v-model="form.plainPassword.value.value"
          :error="form.plainPassword.errorMessage.value"
          width="w-full"
        />

        <BaseInput
          id="confirmPassword"
          :label="$t('auth.resetPassword.form.confirmPassword')"
          type="password"
          :placeholder="$t('auth.signIn.form.passwordPlaceholder')"
          v-model="form.confirmPassword.value.value"
          :error="form.confirmPassword.errorMessage.value"
          width="w-full"
        />
        <div v-if="errorToReset" class="p-3 bg-red-50 border border-red-200 rounded-md">
          <p class="text-sm text-red-600 text-center">
            {{ $t('auth.resetPassword.error') }}
          </p>
        </div>
        <BaseButton type="submit" :loading="isLoading" :disabled="isDisabled" width="w-full">
          {{
            isLoading
              ? $t('auth.resetPassword.form.submiting') + ' ...'
              : $t('auth.resetPassword.form.submit')
          }}
        </BaseButton>
      </form>
      <template v-else>
        <div class="text-center dark:color-warning-200500">
          <span>{{ $t('auth.resetPassword.invalidToken') }}</span>
        </div>

        <router-link
          append
          class="hover:text-main-hover cursor-pointer font-medium transition-colors mt-8 md:mt-12 2xl:mt-14 text-center text-md block"
          :to="{ name: RoutesKey.AUTH_FORGOT_PASSWORD }"
        >
          {{ $t('auth.resetPassword.askANewLink') }} ?
        </router-link>
      </template>
    </div>
  </AuthContainer>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { ref, onBeforeMount, computed } from 'vue'
import AuthContainer from './AuthContainer.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { UserValidator } from '@/services/userAndAuth/forms/UserValidator'
import { AuthenticationService } from '@/services/userAndAuth/AuthenticationService'
import { useField, useForm } from 'vee-validate' //
import { useRoute, useRouter } from 'vue-router'
import { toTypedSchema } from '@vee-validate/zod' //
import { RoutesKey } from '@/models/enums/app/RoutesKey'
import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'
import { POSITION, useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const route = useRoute()
const isLoading = ref(false)
const verifyingToken = ref(true)
const invalidToken = ref<boolean>(false)
const token = computed(() => route.query.token as string | null)
const errorToReset = ref(false)
const { t } = useI18n()
const toast = useToast()
const validationSchema = toTypedSchema(
  UserValidator.refinePasswordMatch(z.object(UserValidator.passwordsObject())),
)

const { handleSubmit, meta } = useForm({
  validationSchema,
  validateOnMount: false,
})

const form = {
  plainPassword: useField<string>('plainPassword', undefined, { validateOnValueUpdate: true }),
  confirmPassword: useField<string>('confirmPassword', undefined, { validateOnValueUpdate: true }),
}

const isDisabled = computed(() => {
  return isLoading.value || !meta.value.valid
})

const onSubmit = handleSubmit(async (values) => {
  if (token.value) {
    isLoading.value = true
    errorToReset.value = false
    try {
      await AuthenticationService.resetPassword(token.value, values.plainPassword)

      toast.success(t('auth.resetPassword.success'), {
        timeout: 3000,
        position: POSITION.TOP_CENTER,
      })
      router.replace({ name: RoutesKey.AUTH_RESET_PASSWORD_OK })
    } catch (e) {
      errorToReset.value = true
      console.error(e)
    } finally {
      isLoading.value = false
    }
  }
})

onBeforeMount(async () => {
  await router.isReady()
  if (token.value) {
    try {
      await AuthenticationService.checkResetPasswordToken(token.value)
      invalidToken.value = false
    } catch {
      invalidToken.value = true
    }
  } else {
    invalidToken.value = true
  }
  verifyingToken.value = false
})
</script>
